import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { doc, getDoc, setDoc, Timestamp } from "firebase/firestore";
import { defaultSettings, defaultWorkPattern, defaultShiftPatterns, toMinutes } from "../utils/payroll";

// 会社ごとの給与設定。初期値は労働基準法の原則どおりにしてあり、ここから変更できる。

const DOW = ["日", "月", "火", "水", "木", "金", "土"];

function PayrollSettings({ companyId, user, onBack }) {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "payrollSettings", companyId));
        const data = snap.exists()
          ? { ...defaultSettings(companyId), ...snap.data() }
          : defaultSettings(companyId);
        if (alive) setS(data);
      } catch (e) {
        console.error("給与設定の取得に失敗しました:", e);
        if (alive) { setS(defaultSettings(companyId)); setError("設定の読み込みに失敗しました。"); }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [companyId]);

  const set    = (k, v) => setS(p => ({ ...p, [k]: v }));
  const setNum = (k, v) => setS(p => ({ ...p, [k]: v === "" ? 0 : Number(v) }));

  const toggleDow = (d) => {
    const cur = s.restDows || [];
    set("restDows", cur.includes(d) ? cur.filter(x => x !== d) : [...cur, d].sort());
  };

  const setBracket = (i, key, v) => {
    const next = [...(s.taxBrackets || [])];
    next[i] = { ...next[i], [key]: v === "" ? 0 : Number(v) };
    set("taxBrackets", next);
  };

  const save = async () => {
    setError(""); setSaved(false); setSaving(true);
    try {
      await setDoc(doc(db, "payrollSettings", companyId), {
        ...s,
        companyId,
        updatedAt: Timestamp.now(),
        updatedBy: user.email,
      }, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      console.error("給与設定の保存に失敗しました:", e);
      setError("保存に失敗しました。権限をご確認ください。");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={st.container}><p style={{ color: "#999" }}>読み込み中...</p></div>;

  const num = (key, label, unit, hint) => (
    <div style={st.field}>
      <label style={st.label}>{label} {hint && <span style={st.hint}>{hint}</span>}</label>
      <div style={st.inputRow}>
        <input style={st.input} type="number" step="any" value={s[key]} onChange={e => setNum(key, e.target.value)} />
        {unit && <span style={st.unit}>{unit}</span>}
      </div>
    </div>
  );

  return (
    <div style={st.container}>
      <button style={st.back} onClick={onBack}>← 給与計算に戻る</button>
      <h2 style={st.title}>⚙️ 給与設定</h2>
      <p style={st.lead}>初期値は労働基準法の原則どおりです。会社の規程に合わせて変更してください。</p>

      <div style={st.section}>締め日・支給日</div>
      <div style={st.grid}>
        {num("closingDay", "締め日", "日", "31で末日締め")}
        {num("payDay", "支給日", "日")}
        <div style={st.field}>
          <label style={st.label}>支給月</label>
          <div style={st.chipRow}>
            {[{ v: 0, l: "当月" }, { v: 1, l: "翌月" }].map(o => (
              <button key={o.v} type="button"
                style={{ ...st.chip, ...(s.payMonthOffset === o.v ? st.chipOn : {}) }}
                onClick={() => set("payMonthOffset", o.v)}>{o.l}</button>
            ))}
          </div>
        </div>
      </div>

      <div style={st.section}>所定労働時間（会社標準）</div>
      <p style={st.note}>
        曜日ごとの始業・終業・休憩を決めます。ここが「本来働くはずの時間」になり、
        これを超えた分が残業として扱われます。社員ごとに違う場合は、社員管理の給与情報で個別に設定できます。
      </p>
      <WorkPatternEditor pattern={s.workPattern} onChange={v => set("workPattern", v)} />

      <div style={st.section}>法定の上限・単価</div>
      <div style={st.grid}>
        {num("dailyHours", "1日の法定労働時間", "時間", "これを超えると1.25倍")}
        {num("weeklyHours", "1週の法定労働時間", "時間", "これを超えると1.25倍")}
        {num("monthlyStdHours", "月平均所定労働時間", "時間", "残業単価の算定に使用")}
        <div style={st.field}>
          <label style={st.label}>週の起算曜日</label>
          <div style={st.chipRow}>
            {DOW.map((d, i) => (
              <button key={i} type="button"
                style={{ ...st.chip, ...(s.weekStartDow === i ? st.chipOn : {}) }}
                onClick={() => set("weekStartDow", i)}>{d}</button>
            ))}
          </div>
        </div>
      </div>

      <div style={st.section}>休憩</div>
      <label style={st.check}>
        <input type="checkbox" checked={!!s.autoBreak} onChange={e => set("autoBreak", e.target.checked)} style={st.cb} />
        労働時間に応じて休憩を自動控除する
      </label>
      <p style={st.note}>打刻は出勤・退勤の2種類のみのため、休憩は自動で差し引きます。</p>
      {s.autoBreak && (
        <div style={st.grid}>
          {num("breakOver6", "6時間超のとき", "分")}
          {num("breakOver8", "8時間超のとき", "分")}
        </div>
      )}

      <div style={st.section}>割増率</div>
      <div style={st.grid}>
        {num("overtimeRate", "時間外", "倍", "法定 1.25")}
        {num("nightExtraRate", "深夜の加算", "倍", "法定 0.25")}
        {num("holidayRate", "法定休日", "倍", "法定 1.35")}
        {num("overtimeRate60", "月60時間超", "倍", "法定 1.5")}
        {num("monthlyOvertimeThreshold", "60時間超の基準", "時間", "法定 60")}
        {num("nightStartHour", "深夜の開始", "時")}
        {num("nightEndHour", "深夜の終了", "時")}
      </div>

      <div style={st.section}>休日</div>
      <div style={st.field}>
        <label style={st.label}>法定休日の曜日</label>
        <div style={st.chipRow}>
          {DOW.map((d, i) => (
            <button key={i} type="button"
              style={{ ...st.chip, ...(s.legalHolidayDow === i ? st.chipOn : {}) }}
              onClick={() => set("legalHolidayDow", i)}>{d}</button>
          ))}
        </div>
      </div>
      <div style={st.field}>
        <label style={st.label}>所定休日 <span style={st.hint}>複数選択できます</span></label>
        <div style={st.chipRow}>
          {DOW.map((d, i) => (
            <button key={i} type="button"
              style={{ ...st.chip, ...((s.restDows || []).includes(i) ? st.chipOn : {}) }}
              onClick={() => toggleDow(i)}>{d}</button>
          ))}
        </div>
      </div>

      <div style={st.section}>シフトの勤務パターン</div>
      <p style={st.note}>
        シフト表で割り当てる勤務の種類です。ここで決めた時間が、その日の所定労働時間になります。
      </p>
      {(s.shiftPatterns || defaultShiftPatterns()).map((sp, i) => (
        <div key={sp.key} style={st.shiftRow}>
          <input style={st.shiftName} value={sp.name} placeholder="名称"
            onChange={e => {
              const next = [...(s.shiftPatterns || defaultShiftPatterns())];
              next[i] = { ...next[i], name: e.target.value };
              set("shiftPatterns", next);
            }} />
          <input style={st.shiftTime} type="time" value={sp.start || ""}
            onChange={e => {
              const next = [...(s.shiftPatterns || defaultShiftPatterns())];
              next[i] = { ...next[i], start: e.target.value };
              set("shiftPatterns", next);
            }} />
          <span style={st.shiftSep}>〜</span>
          <input style={st.shiftTime} type="time" value={sp.end || ""}
            onChange={e => {
              const next = [...(s.shiftPatterns || defaultShiftPatterns())];
              next[i] = { ...next[i], end: e.target.value };
              set("shiftPatterns", next);
            }} />
          <input style={st.shiftBreak} type="number" min="0" value={sp.breakMin ?? 0}
            onChange={e => {
              const next = [...(s.shiftPatterns || defaultShiftPatterns())];
              next[i] = { ...next[i], breakMin: Number(e.target.value || 0) };
              set("shiftPatterns", next);
            }} />
          <span style={st.shiftUnit}>分休憩</span>
          <button type="button" style={st.shiftDel}
            onClick={() => set("shiftPatterns", (s.shiftPatterns || defaultShiftPatterns()).filter((_, x) => x !== i))}>
            削除
          </button>
        </div>
      ))}
      <button type="button" style={st.shiftAdd}
        onClick={() => set("shiftPatterns", [
          ...(s.shiftPatterns || defaultShiftPatterns()),
          { key: `sp_${Date.now()}`, name: "", start: "09:00", end: "18:00", breakMin: 60, color: "#16a085" },
        ])}>
        ＋ 勤務パターンを追加
      </button>

      <div style={st.section}>社会保険料（本人負担分）</div>
      <p style={st.note}>標準報酬月額に対する率です。健康保険料は都道府県によって異なります。協会けんぽの料額表でご確認ください。</p>
      <div style={st.grid}>
        {num("healthRate", "健康保険", "%")}
        {num("careRate", "介護保険", "%", "40歳以上")}
        {num("pensionRate", "厚生年金", "%")}
        {num("employmentRate", "雇用保険", "%", "総支給額に対して")}
      </div>

      <div style={st.section}>所得税</div>
      <div style={st.field}>
        <label style={st.label}>計算方法</label>
        <div style={st.chipRow}>
          {[{ v: "auto", l: "自動計算" }, { v: "manual", l: "手入力" }].map(o => (
            <button key={o.v} type="button"
              style={{ ...st.chip, ...(s.incomeTaxMode === o.v ? st.chipOn : {}) }}
              onClick={() => set("incomeTaxMode", o.v)}>{o.l}</button>
          ))}
        </div>
      </div>

      {s.incomeTaxMode === "auto" && (
        <>
          <div style={s.taxTableVerified ? st.okBox : st.warnBox}>
            {s.taxTableVerified ? (
              <>✅ 税額表は確認済みとして設定されています。{s.taxTableNote && `（${s.taxTableNote}）`}</>
            ) : (
              <>
                ⚠️ <strong>下の税額表は初期値であり、確認が済んでいません。</strong>
                国税庁の「源泉徴収税額表（電子計算機等を使用して源泉徴収税額を計算する方法）」と必ず照合してください。
                誤った値のまま支給に使うと、源泉徴収額が不足または過大になります。
              </>
            )}
          </div>

          <div style={st.grid}>
            {num("basicDeduction", "基礎的な控除（月額）", "円")}
            {num("dependentDeduction", "扶養親族等1人あたり（月額）", "円")}
            {num("commuteTaxFreeLimit", "通勤手当の非課税限度（月額）", "円")}
          </div>

          <label style={st.label}>税額表</label>
          <div style={st.tableWrap}>
            <table style={st.table}>
              <thead>
                <tr><th style={st.th}>この金額を超える</th><th style={st.th}>税率(%)</th><th style={st.th}>控除額(円)</th></tr>
              </thead>
              <tbody>
                {(s.taxBrackets || []).map((b, i) => (
                  <tr key={i}>
                    <td style={st.td}><input style={st.cell} type="number" value={b.over}
                      onChange={e => setBracket(i, "over", e.target.value)} /></td>
                    <td style={st.td}><input style={st.cell} type="number" step="any" value={b.rate}
                      onChange={e => setBracket(i, "rate", e.target.value)} /></td>
                    <td style={st.td}><input style={st.cell} type="number" value={b.deduct}
                      onChange={e => setBracket(i, "deduct", e.target.value)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <label style={st.check}>
            <input type="checkbox" checked={!!s.taxTableVerified}
              onChange={e => set("taxTableVerified", e.target.checked)} style={st.cb} />
            国税庁の資料と照合し、上の内容が正しいことを確認した
          </label>
          <input style={st.input} value={s.taxTableNote || ""} onChange={e => set("taxTableNote", e.target.value)}
            placeholder="確認した資料・年度など（例：令和8年分 月額表）" />
        </>
      )}

      {error && <p style={st.error}>{error}</p>}

      <div style={st.btnRow}>
        <button style={st.saveBtn} onClick={save} disabled={saving}>{saving ? "保存中..." : "保存する"}</button>
        {saved && <span style={{ color: "#27ae60", alignSelf: "center" }}>✅ 保存しました</span>}
      </div>
    </div>
  );
}


/**
 * 曜日別の所定労働時間を編集する表。会社標準と個人別のどちらでも使う。
 */
export function WorkPatternEditor({ pattern, onChange }) {
  const rows = Array.isArray(pattern) && pattern.length === 7 ? pattern : defaultWorkPattern();
  const set = (i, key, value) => {
    const next = rows.map((r, idx) => idx === i ? { ...r, [key]: value } : r);
    onChange(next);
  };
  const hours = (r) => {
    if (!r.work) return "－";
    const s = toMinutes(r.start), e = toMinutes(r.end);
    if (s === null || e === null) return "－";
    const span = e > s ? e - s : (24 * 60 - s) + e;
    const net = Math.max(0, span - (Number(r.breakMin) || 0));
    return `${(net / 60).toFixed(2)}h`;
  };
  const total = rows.reduce((a, r) => {
    if (!r.work) return a;
    const s = toMinutes(r.start), e = toMinutes(r.end);
    if (s === null || e === null) return a;
    const span = e > s ? e - s : (24 * 60 - s) + e;
    return a + Math.max(0, span - (Number(r.breakMin) || 0));
  }, 0);

  return (
    <div>
      <div style={pe.tableWrap}>
        <table style={pe.table}>
          <thead>
            <tr>
              <th style={pe.th}>曜日</th>
              <th style={pe.th}>所定労働日</th>
              <th style={pe.th}>始業</th>
              <th style={pe.th}>終業</th>
              <th style={pe.th}>休憩(分)</th>
              <th style={pe.th}>実働</th>
            </tr>
          </thead>
          <tbody>
            {DOW_LABELS.map((d, i) => (
              <tr key={i} style={i === 0 || i === 6 ? pe.weekend : undefined}>
                <td style={pe.td}>{d}</td>
                <td style={pe.td}>
                  <input type="checkbox" checked={!!rows[i].work}
                    onChange={e => set(i, "work", e.target.checked)} style={pe.cb} />
                </td>
                <td style={pe.td}>
                  <input type="time" style={pe.time} value={rows[i].start || ""} disabled={!rows[i].work}
                    onChange={e => set(i, "start", e.target.value)} />
                </td>
                <td style={pe.td}>
                  <input type="time" style={pe.time} value={rows[i].end || ""} disabled={!rows[i].work}
                    onChange={e => set(i, "end", e.target.value)} />
                </td>
                <td style={pe.td}>
                  <input type="number" min="0" style={pe.num} value={rows[i].breakMin ?? 0} disabled={!rows[i].work}
                    onChange={e => set(i, "breakMin", Number(e.target.value || 0))} />
                </td>
                <td style={{ ...pe.td, textAlign: "right", color: "#2c3e50" }}>{hours(rows[i])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={pe.note}>
        週の所定労働時間の合計：<strong>{(total / 60).toFixed(2)}時間</strong>
        {total > 40 * 60 && <span style={{ color: "#e74c3c" }}>　※ 法定の週40時間を超えています</span>}
      </p>
    </div>
  );
}

const DOW_LABELS = ["日", "月", "火", "水", "木", "金", "土"];

const pe = {
  tableWrap: { overflowX: "auto", marginBottom: "6px" },
  table: { width: "100%", borderCollapse: "collapse", backgroundColor: "#fff", fontSize: "13px" },
  th: { backgroundColor: "#2c3e50", color: "#fff", padding: "8px 10px", fontSize: "12px", textAlign: "left", whiteSpace: "nowrap" },
  td: { borderBottom: "1px solid #eee", padding: "5px 8px", whiteSpace: "nowrap" },
  weekend: { backgroundColor: "#fafbfc" },
  cb: { width: "16px", height: "16px" },
  time: { padding: "5px 7px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "13px" },
  num: { width: "70px", padding: "5px 7px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "13px", textAlign: "right" },
  note: { fontSize: "13px", color: "#5d6d7e", marginTop: 0, marginBottom: "12px" },
};

const st = {
  container: { padding: "32px", maxWidth: "800px", margin: "0 auto" },
  back: { background: "none", border: "none", color: "#2980b9", cursor: "pointer", fontSize: "14px", padding: 0, marginBottom: "12px" },
  title: { color: "#2c3e50", marginBottom: "6px" },
  lead: { color: "#7f8c8d", fontSize: "14px", marginTop: 0, marginBottom: "20px" },
  section: { color: "#2c3e50", fontWeight: "bold", fontSize: "15px", margin: "26px 0 12px", paddingBottom: "6px", borderBottom: "2px solid #f0f4f8" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "14px" },
  field: { marginBottom: "12px" },
  label: { display: "block", marginBottom: "6px", color: "#2c3e50", fontSize: "13px", fontWeight: "bold" },
  hint: { fontWeight: "normal", color: "#999", fontSize: "11px" },
  inputRow: { display: "flex", alignItems: "center", gap: "8px" },
  input: { flex: 1, width: "100%", padding: "10px 12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box" },
  unit: { fontSize: "13px", color: "#7f8c8d", flexShrink: 0 },
  chipRow: { display: "flex", gap: "6px", flexWrap: "wrap" },
  chip: { padding: "7px 14px", border: "2px solid #ddd", borderRadius: "8px", backgroundColor: "#fff", cursor: "pointer", fontSize: "13px", color: "#7f8c8d" },
  chipOn: { border: "2px solid #2c3e50", backgroundColor: "#2c3e50", color: "#fff", fontWeight: "bold" },
  check: { display: "flex", alignItems: "center", fontSize: "14px", color: "#2c3e50", cursor: "pointer", marginBottom: "8px" },
  cb: { width: "17px", height: "17px", marginRight: "8px" },
  note: { fontSize: "12px", color: "#95a5a6", lineHeight: "1.7", marginTop: 0, marginBottom: "12px" },
  warnBox: { backgroundColor: "#fff6e5", border: "1px solid #f5c26b", borderRadius: "10px", padding: "14px 18px", marginBottom: "16px", fontSize: "13px", color: "#8a5a00", lineHeight: "1.8" },
  okBox: { backgroundColor: "#eafaf1", border: "1px solid #a9dfbf", borderRadius: "10px", padding: "14px 18px", marginBottom: "16px", fontSize: "13px", color: "#1e7e46", lineHeight: "1.8" },
  tableWrap: { overflowX: "auto", marginBottom: "12px" },
  table: { width: "100%", borderCollapse: "collapse", backgroundColor: "#fff" },
  th: { backgroundColor: "#2c3e50", color: "#fff", padding: "8px 10px", fontSize: "12px", textAlign: "left" },
  td: { borderBottom: "1px solid #eee", padding: "4px 6px" },
  cell: { width: "100%", padding: "7px 9px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "13px", textAlign: "right", boxSizing: "border-box" },
  error: { color: "#e74c3c", fontSize: "14px", marginTop: "12px" },
  btnRow: { display: "flex", gap: "12px", marginTop: "28px" },
  saveBtn: { padding: "12px 32px", backgroundColor: "#2c3e50", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
  shiftRow: { display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px", flexWrap: "wrap" },
  shiftName: { width: "120px", padding: "8px 10px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px" },
  shiftTime: { padding: "8px 10px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px" },
  shiftSep: { color: "#7f8c8d" },
  shiftBreak: { width: "70px", padding: "8px 10px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", textAlign: "right" },
  shiftUnit: { fontSize: "13px", color: "#7f8c8d" },
  shiftDel: { padding: "7px 14px", backgroundColor: "#fff", color: "#e74c3c", border: "1px solid #e74c3c", borderRadius: "8px", cursor: "pointer", fontSize: "13px" },
  shiftAdd: { marginTop: "4px", padding: "9px 18px", backgroundColor: "#fff", color: "#2c3e50", border: "1px dashed #99a", borderRadius: "8px", cursor: "pointer", fontSize: "13px" },
};

export default PayrollSettings;
